import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, Card as CardIcon, Flash, Notification } from "iconsax-reactjs"
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs"
import { useMemo } from "react"

import { fetchAllNotifications, fetchUnreadCount, readAllNotification } from "@/api"
import { NotificationFilterView } from "@/components/others/notification/notification-filter-view"
import { NotificationList } from "@/components/others/notification/notification-list"
import { GoBack } from "@/components/shared"
import { Logo } from "@/components/shared/layout/logo"
import { Badge, Button, Sheet, SheetContent, SheetTrigger } from "@/components/ui"
import ScreenLayout from "@/layouts/screen"
import { useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { sendHaptic, sendPermissionNotification } from "@/modules/helpers/utils"

const notificationStats = [
  {
    id: "new-campaign",
    label: "Chiến dịch",
    description: "Cập nhật thông tin từ các chiến dịch",
    icon: <Flash className="w-5 h-5" />
  },
  {
    id: "financial",
    label: "Thanh toán",
    description: "Thông báo tình trạng thanh toán và tài chính",
    icon: <CardIcon className="w-5 h-5" />
  },
  {
    id: "zzp",
    label: "Cập nhật ZZP",
    description: "Cảm nhận và ý kiến của bạn khi sử dụng ZZP",
    icon: <Logo w={24} h={24} />,
    disabled: true
  }
]

export function PopUpNotification() {
  const [open, setOpen] = useQueryState("open_notification", parseAsBoolean.withDefault(false))
  const { permissions } = useCurrentUser()

  const [filter, setFilter] = useQueryState(
    "open_notification_filter",
    parseAsString.withDefault("")
  )

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, forceRefetch } =
    useInfiniteScrollQuery({
      queryKey: ["notification"],
      queryFn: fetchAllNotifications,
      params: {}
    })

  // Fetch unread counts
  const { data: unreadCountData, refetch: refetchUnreadCount } = useQuery({
    queryKey: ["notification-unread-count"],
    queryFn: async () => {
      const response = await fetchUnreadCount()
      return response.data.data
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  })

  const notifications = useMemo(() => {
    return data?.list || []
  }, [data?.list])

  const statCounts = useMemo(() => {
    return {
      important: unreadCountData?.totalUnreadImportant || 0,
      campaigns: unreadCountData?.totalUnreadNewCampaign || 0,
      financial: unreadCountData?.totalUnreadFinancial || 0,
      zzp: 0 // keep static for now (API not available yet)
    }
  }, [unreadCountData])

  const totalUnreadCount = useMemo(() => {
    return (
      (unreadCountData?.totalUnreadImportant || 0) +
      (unreadCountData?.totalUnreadNewCampaign || 0) +
      (unreadCountData?.totalUnreadFinancial || 0)
    )
  }, [unreadCountData])

  const isOpenUnreadCountByStatus = useMemo(() => {
    if (!!filter && !!open) {
      return true
    }
    return false
  }, [filter, open])

  const handleRefresh = async () => {
    await Promise.all([
      forceRefetch?.(),
      // Refetch unread count to sync badges
      refetchUnreadCount()
    ])
  }

  return (
    <Sheet open={open} modal>
      <SheetTrigger
        className="flex items-center gap-3 p-2 rounded-full"
        onClick={() => {
          setOpen(true)
          sendHaptic()
        }}
      >
        <div className="relative flex h-9 w-9 items-center justify-center rounded-full">
          <Notification size={20} color="#0091AA" />
          {!!totalUnreadCount && (
            <span className="!text-[8px] flex justify-center items-center font-medium text-white bg-red-500 absolute aspect-square min-w-4 w-fit px-0.5 rounded-full -top-1 -right-1">
              {totalUnreadCount >= 99 ? "+99" : totalUnreadCount}
            </span>
          )}
        </div>
      </SheetTrigger>

      <SheetContent className="w-full !p-0 overflow-hidden" side="right" hiddenClose>
        {!!isOpenUnreadCountByStatus && (
          <NotificationFilterView
            filter={filter}
            setFilter={setFilter}
            isOpen={!!isOpenUnreadCountByStatus}
          />
        )}
        <ScreenLayout transition="none">
          <div className="flex flex-col sticky top-0 z-10">
            {!permissions?.includes?.("NOTIFICATION") ? (
              <div className="bg-gradient-to-r from-primary to-secondary transition-transform p-2 flex gap-2 justify-center items-center flex-shrink-0">
                <div
                  onClick={() => {
                    sendHaptic()
                    setOpen(false)
                  }}
                  className=""
                >
                  <div className="flex items-center gap-4 py-1">
                    <Button variant="ghost" size="sm" className="p-1 rounded-full bg-white w-7 h-7">
                      <ArrowLeft size={20} />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-white leading-relaxed">
                  Đồng ý nhận thông báo cập nhập những thông tin mới nhất về các chiến dịch, hoạt
                  động và nhiều nội dung khác nữa.{" "}
                </p>
                <p
                  className="font-medium h-fit text-xs whitespace-nowrap bg-white px-2 py-1 my-1 rounded-full text-gray-600"
                  onClick={() => sendPermissionNotification()}
                >
                  Đồng ý
                </p>
                <GoBack
                  onClick={() => {
                    sendHaptic()
                    setOpen(false)
                  }}
                  hidden
                  title="Thông báo"
                />
              </div>
            ) : (
              <div className="bg-white flex-shrink-0">
                <GoBack
                  onClick={() => {
                    sendHaptic()
                    setOpen(false)
                  }}
                  title="Thông báo"
                />
              </div>
            )}

            {/* Category Cards */}
            <div className="bg-white flex-shrink-0">
              {notificationStats.map((stat) => {
                const count = statCounts[stat.id as keyof typeof statCounts] || 0
                return (
                  <div
                    key={stat.id}
                    className={cn(
                      "px-4 py-2 cursor-pointer border-b border-gray-100 transition-all duration-200 active:bg-gray-50"
                    )}
                    onClick={() => {
                      if (stat.disabled) return
                      sendHaptic()
                      setFilter(stat.id)
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className={cn("p-1 rounded-lg flex-shrink-0 text-primary")}>
                          {stat.id === "zzp" ? <Logo w={24} h={24} /> : stat.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-1">
                            <span className="font-normal text-sm text-gray-900">{stat.label}</span>
                            {count > 0 && (
                              <Badge
                                variant="secondary"
                                className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-medium min-w-[20px] h-5 flex items-center justify-center"
                              >
                                {count >= 99 ? "+99" : count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {stat.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Recent Notifications Header */}
            <div className="px-4 py-3 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <h2 className="text-sm font-semibold text-gray-900">Gần đây</h2>
              <h2
                className="text-xs text-primary"
                onClick={() =>
                  readAllNotification().finally(() => {
                    refetchUnreadCount()
                  })
                }
              >
                Đánh dấu đã đọc tất cả ({totalUnreadCount})
              </h2>
            </div>
          </div>

          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            onRefresh={handleRefresh}
          />
        </ScreenLayout>
      </SheetContent>
    </Sheet>
  )
}
